import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { earnSummaryBunja } from '@flipster/features/src/shared/earn/state';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { mintPreviewBunja } from './state';

interface Props {
  onClose: () => void;
  onConfirm: () => void;
}

const Preview = ({ onClose, onConfirm }: Props) => {
  const t = useTranslations();
  const {
    fromCurrencyAtom,
    toCurrencyAtom,
    fromAmountAtom,
    toAmountAtom,
    estimateTimeAtom,
    postMintConfirmAtom,
    flowTypeAtom,
  } = useBunja(mintPreviewBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const fromAmount = useAtomValue(fromAmountAtom);
  const toAmount = useAtomValue(toAmountAtom);
  const fromCurrency = useAtomValue(fromCurrencyAtom);
  const toCurrency = useAtomValue(toCurrencyAtom);
  const estimateTime = useAtomValue(estimateTimeAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const postMintConfirm = useAtomValue(postMintConfirmAtom);
  const flowType = useAtomValue(flowTypeAtom);

  const isEarningAsset = useMemo(() => {
    return earnSummary?.currencies.some((c) => c.currency === toCurrency);
  }, [earnSummary, toCurrency]);

  const [isChecked, setIsChecked] = useState(false);

  const confirmPreview = async () => {
    segment.track(EarnEvent.EarnPreviewConfirmClick, {
      manage_type: flowType,
      from_currency: fromCurrency,
      to_currency: toCurrency,
      from_amount: fromAmount,
      to_amount: toAmount,
    });
    await postMintConfirm.mutateAsync();
    onConfirm?.();
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t('Common.r251008.Confirm_your_request')} />
      </Modal.Header>
      <Modal.Content css={stack.raw({ gap: 20 })}>
        <div className={stack({ gap: 12 })}>
          <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            {t('Common.r251008.From')}
          </div>
          <div className={hstack({ justify: 'space-between' })}>
            <CurrencyDisplay
              currency={fromCurrency}
              icon={<CurrencyDisplay.Icon size={24} space={8} />}
              mainLabel={
                <CurrencyDisplay.Label
                  value='shortName'
                  css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
                />
              }
            />
            <Formatted.Number
              value={fromAmount}
              suffix={fromCurrency}
              css={{ textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-5' }}
            />
          </div>
        </div>
        <div className={stack({ gap: 12 })}>
          <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            {t('Common.r251008.To_estimated')}
          </div>
          <div className={hstack({ justify: 'space-between' })}>
            <CurrencyDisplay
              currency={toCurrency}
              icon={<CurrencyDisplay.Icon size={24} space={8} />}
              mainLabel={
                <CurrencyDisplay.Label
                  value='shortName'
                  css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
                />
              }
            />
            <Formatted.Number
              value={toAmount}
              suffix={toCurrency}
              css={{ textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-5' }}
            />
          </div>
        </div>
        <div
          className={stack({
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-1',
            rounded: 8,
            gap: 8,
            px: 16,
            py: 12,
          })}
        >
          <div className={hstack({ justify: 'space-between' })}>
            <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
              {t('Common.r251008.Processing_time')}
            </span>
            <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
              {t('Common.r251008.Est_minute_m', { minute: estimateTime })}
            </span>
          </div>
          {isEarningAsset && (
            <div className={hstack({ justify: 'space-between' })}>
              <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                {t('Common.r251008.Interest_accrual')}
              </span>
              <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                {t('Common.r251008.Within_one_hour_after_request_completion')}
              </span>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer css={stack.raw({ gap: 16 })}>
        <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5', px: 16 })}>
          <Checkbox
            onChange={() => {
              setIsChecked(!isChecked);
            }}
          >
            {flowType === 'MINT'
              ? t('Mint.r251008.I_understand_minting_requires')
              : t('Redeem.r251008.I_understand_redemption_requires')}
          </Checkbox>
        </div>
        <div
          className={css({
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            '& > button': { tabletDown: { flex: 1 }, tablet: { minW: 180 } },
          })}
        >
          <Button
            label={t('Common.r250401.Cancel')}
            hierarchy='secondary'
            size='large'
            color='neutral'
            onClick={onClose}
          />
          <Button
            label={t('Common.r250401.Confirm')}
            hierarchy='primary'
            size='large'
            color='accent'
            onClick={confirmPreview}
            disabled={!isChecked}
          />
        </div>
      </Modal.Footer>
    </>
  );
};

export default Preview;
